import type { PaginatedResultOfGetPolicyResponse } from "@/openapi/generated/models";

const STORAGE_KEY_PREFIX = "waiver-signatures:v1";

export type PendingWaiverSignatures = Record<number, string>;

export function getWaiverSignaturesSessionKey(
  tenantId: number,
  userId: number,
): string {
  return `${STORAGE_KEY_PREFIX}:${tenantId}:${userId}`;
}

export function readPendingWaiverSignatures(
  tenantId: number,
  userId: number,
): PendingWaiverSignatures {
  if (typeof window === "undefined") return {};

  try {
    const raw = sessionStorage.getItem(getWaiverSignaturesSessionKey(tenantId, userId));
    if (!raw) return {};

    const parsed = JSON.parse(raw) as PendingWaiverSignatures;
    if (!parsed || typeof parsed !== "object") return {};

    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([key, value]) => !Number.isNaN(Number(key)) && typeof value === "string",
      ).map(([key, value]) => [Number(key), value]),
    );
  } catch {
    return {};
  }
}

export function writePendingWaiverSignatures(
  tenantId: number,
  userId: number,
  signatures: PendingWaiverSignatures,
): void {
  if (typeof window === "undefined") return;

  const key = getWaiverSignaturesSessionKey(tenantId, userId);
  if (Object.keys(signatures).length === 0) {
    sessionStorage.removeItem(key);
    return;
  }

  sessionStorage.setItem(key, JSON.stringify(signatures));
}

export function rememberPendingWaiverSignature(
  tenantId: number,
  userId: number,
  policyId: number,
  acceptedAt: Date,
): void {
  const pending = readPendingWaiverSignatures(tenantId, userId);
  pending[policyId] = acceptedAt.toISOString();
  writePendingWaiverSignatures(tenantId, userId, pending);
}

export function mergePoliciesWithPendingSignatures(
  tenantId: number,
  userId: number,
  data: PaginatedResultOfGetPolicyResponse,
): PaginatedResultOfGetPolicyResponse {
  if (!data.items?.length || typeof window === "undefined") return data;

  const pending = readPendingWaiverSignatures(tenantId, userId);
  if (Object.keys(pending).length === 0) return data;

  const nextPending = { ...pending };
  let changed = false;

  const items = data.items.map((item) => {
    const policyId = Number(item.policyId);

    if (item.acceptedAt) {
      if (nextPending[policyId]) {
        delete nextPending[policyId];
        changed = true;
      }
      return item;
    }

    const storedAcceptedAt = pending[policyId];
    if (!storedAcceptedAt) return item;

    changed = true;
    return { ...item, acceptedAt: new Date(storedAcceptedAt) };
  });

  if (changed && Object.keys(nextPending).length !== Object.keys(pending).length) {
    writePendingWaiverSignatures(tenantId, userId, nextPending);
  }

  return changed ? { ...data, items } : data;
}
